import {Injectable} from '@angular/core';
import {TransactionTypeTexts} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {TransactionTypeCode} from '@app/services/transactionTypes/transaction-type-code';
import {
  ConfiguratorUriParameterService,
  DcpApiService,
  ITransactionTypeService,
  JsonService,
  TransactionListCta,
  TransactionType,
  TypeValuePair,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root';

import {combineLatest, Observable, ReplaySubject} from 'rxjs';
import {map} from 'rxjs/operators';

export const TRANSLATIONS: string = 'translations';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TransactionTypeService implements ITransactionTypeService {
  transactionTypes$: ReplaySubject<TransactionType[]> = new ReplaySubject<TransactionType[]>(1);

  constructor(
    private jsonService: JsonService,
    private uriParamService: ConfiguratorUriParameterService,
    private dcpApiService: DcpApiService
  ) {
    this.fetchTransactionTypes()
      .pipe(untilDestroyed(this))
      .subscribe((tt) => this.transactionTypes$.next(tt));
  }

  public getTransactionTypes$(): Observable<TransactionType[]> {
    return this.transactionTypes$.asObservable();
  }

  getTransactionTypeCodeByUrlOrOrderEntry(orderEntry?: OrderEntry): string {
    return this.uriParamService.getAction() || orderEntry?.transactionType;
  }

  /**
   * Returns transaction type object that fits to what is required by the URL or the OrderEntry.
   *
   * @param orderEntry order Entry that contains the transactionType property.
   * Is needed in case the action property is not part of the URL.
   */
  getTransactionTypeByUrlOrOrderEntry(orderEntry?: OrderEntry): Observable<TransactionType> {
    return this.getTransactionTypeConfiguration(this.getTransactionTypeCodeByUrlOrOrderEntry(orderEntry));
  }

  isInitialPurchase(type: string): boolean {
    return type === TransactionTypeCode.ORDER_NEW;
  }

  public getTransactionTypeConfiguration(type: string): Observable<TransactionType> {
    return this.transactionTypes$.pipe(map((tts) => tts.find((tt) => tt.code === type)));
  }

  public getTransactionTypeConfigurationJson(transactionTypeCode: string, jsonType: string): Observable<any> {
    return this.getTransactionTypeConfiguration(transactionTypeCode).pipe(
      map((tt) => {
        const jsonDataList: TypeValuePair[] = tt?.data;
        if (jsonDataList?.length) {
          const rawJsonData: TypeValuePair = jsonDataList.find((json) => json.type === jsonType);
          if (rawJsonData) {
            return this.jsonService.parseSafely(rawJsonData.value);
          }
        }
        return undefined;
      })
    );
  }

  public isNetNew(code: string): Observable<boolean> {
    return this.getTransactionTypeConfiguration(code).pipe(map((tt) => this.isNetNewTransactionType(tt)));
  }

  public isNetNewTransactionType(transactionType: TransactionType): boolean {
    return transactionType?.shouldPrefillCartFromContract === false;
  }

  getTranslations(code: string): Observable<TransactionTypeTexts> {
    return this.getJsonDataByType(code, TRANSLATIONS);
  }

  getJsonDataByType(code: string, type: string): Observable<TransactionTypeTexts> {
    return this.getTransactionTypeConfiguration(code).pipe(
      map((tt) => {
        const jsonDataList: TypeValuePair[] = tt?.data;
        if (jsonDataList?.length) {
          const rawJsonData: TypeValuePair = jsonDataList.find((json) => json.type === type);
          if (rawJsonData) {
            return this.jsonService.parseSafely(rawJsonData.value) as TransactionTypeTexts;
          }
        }
        return undefined;
      })
    );
  }

  /**
   * Takes an array of TransactionListCtas and returns them ordered by the priority with actionType added
   *
   * @return TransactionListCta[] sorted array of CTAs to show on PDP
   * @params slmTransactionTypes: TransactionListCta[] unsorted array of CTAs to show on PDP
   */
  sortSlmTransactionTypesByPriority(slmTransactionTypes: TransactionListCta[]): Observable<TransactionListCta[]> {
    const observables = [];
    for (const slmTransactionType of slmTransactionTypes) {
      slmTransactionType.priority = Infinity; // CTAs with no priority set will be sorted to the back
      observables.push(this.getTransactionTypeConfiguration(slmTransactionType.code));
    }
    return combineLatest(observables).pipe(
      map((transactionTypes: TransactionType[]) => {
        for (const transactionType of transactionTypes.filter(Boolean)) {
          const slmTransactionType = slmTransactionTypes.find((tt) => tt.code === transactionType.code);
          slmTransactionType.actionType = transactionType.actionType;
          try {
            slmTransactionType.priority = JSON.parse(
              transactionType.data.find((d) => d.type === 'priority').value
            ).priority;
          } catch (e) {}
        }
        return slmTransactionTypes.sort((a, b) => a.priority - b.priority);
      })
    );
  }

  private fetchTransactionTypes(): Observable<TransactionType[]> {
    return this.dcpApiService
      .callOcc<{transactionTypes: TransactionType[]}>('transactionTypes')
      .pipe(map((ttWrapper) => ttWrapper?.transactionTypes || []));
  }
}
